import { useEffect, useMemo, useState } from 'react';
import { Ruler, Plus, X } from 'lucide-react';
import type { MoldeMetadata } from '../types';
import { MOLDE_SIZE_FAMILY_OPTIONS, MOLDE_SIZE_OPTIONS_BY_FAMILY, type MoldeSizeFamily } from './catalog';

type SizeDraft = {
  familiaTamanho: MoldeSizeFamily;
  tamanho: string;
};

type SizeSheetProps = {
  open: boolean;
  busy: boolean;
  molde: MoldeMetadata | undefined;
  onClose: () => void;
  onSave: (draft: SizeDraft) => Promise<void>;
};

function getInitialDraft(molde: MoldeMetadata | undefined): SizeDraft {
  return {
    familiaTamanho: (molde?.familiaTamanho as MoldeSizeFamily) || 'tradicional',
    tamanho: molde?.tamanho || '',
  };
}

export function SizeSheet({ open, busy, molde, onClose, onSave }: SizeSheetProps) {
  const [draft, setDraft] = useState<SizeDraft>(getInitialDraft(molde));
  const [isRendered, setIsRendered] = useState(open);
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    setDraft(getInitialDraft(molde));
  }, [molde]);

  useEffect(() => {
    if (open) {
      setIsRendered(true);
      const rafId = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(rafId);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => setIsRendered(false), 180);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const presetOptions = useMemo(
    () => MOLDE_SIZE_OPTIONS_BY_FAMILY[draft.familiaTamanho] ?? [],
    [draft.familiaTamanho]
  );

  if (!isRendered) return null;

  const isCustom = draft.familiaTamanho === 'custom';

  return (
    <div
      className={`absolute inset-0 z-30 flex items-end bg-navy-950/70 backdrop-blur-sm transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <button aria-label="Fechar tamanho do molde" onClick={onClose} className="absolute inset-0" />

      <div
        className={`relative z-10 flex max-h-[66%] w-full flex-col overflow-hidden rounded-t-[22px] border border-navy-700 bg-navy-900 shadow-2xl transition-transform duration-200 ${
          isVisible ? 'translate-y-0' : 'translate-y-8'
        }`}
      >
        <div className="border-b border-navy-700 px-3 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.2em] text-navy-400">Dados do molde</div>
              <h3 className="mt-0.5 flex items-center gap-2 text-base font-semibold text-yellow-400">
                <Ruler size={16} />
                Tamanho
              </h3>
              <div className="mt-0.5 text-[11px] text-navy-300">
                Defina o tamanho comercial deste molde.
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-navy-600 bg-navy-800 p-1.5 text-navy-200 transition-colors hover:bg-navy-700"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
              Familia
            </label>
            <select
              value={draft.familiaTamanho}
              onChange={(event) =>
                setDraft({
                  familiaTamanho: event.target.value as MoldeSizeFamily,
                  tamanho: '',
                })
              }
              className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-yellow-500"
            >
              {MOLDE_SIZE_FAMILY_OPTIONS.map((family) => (
                <option key={family.value} value={family.value}>
                  {family.label}
                </option>
              ))}
            </select>
          </div>

          {!isCustom ? (
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                Tamanho
              </label>
              <select
                value={draft.tamanho}
                onChange={(event) => setDraft((current) => ({ ...current, tamanho: event.target.value }))}
                className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-yellow-500"
              >
                <option value="">Selecione...</option>
                {presetOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                Adicionar tamanho
              </label>
              <div className="relative">
                <Plus size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                <input
                  value={draft.tamanho}
                  onChange={(event) => setDraft((current) => ({ ...current, tamanho: event.target.value }))}
                  placeholder="Ex.: XGG"
                  className="w-full rounded-lg border border-navy-600 bg-navy-800 py-2.5 pl-9 pr-3 text-[13px] text-white outline-none transition-colors placeholder:text-navy-500 focus:border-yellow-500"
                />
              </div>
            </div>
          )}

          <div className="rounded-xl border border-navy-700 bg-navy-950/40 px-3 py-2.5 text-[11px] text-navy-300">
            {draft.tamanho ? (
              <span>
                Tamanho do molde: <span className="font-semibold text-white">{draft.tamanho}</span>
              </span>
            ) : (
              'Nenhum tamanho definido ainda.'
            )}
          </div>
        </div>

        <div className="border-t border-navy-700 px-3 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => void onSave(draft)}
              disabled={busy || !draft.tamanho.trim()}
              className="flex-1 rounded-lg bg-yellow-500 px-4 py-2.5 text-[13px] font-semibold text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-50"
            >
              {busy ? 'Salvando...' : 'Salvar tamanho'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
